/**
 * Plus Codes (Open Location Code) — Google's open-source global addressing system.
 * Spec: github.com/google/open-location-code/blob/main/docs/specification.md
 *
 * v0 implementation: thin wrapper around the well-tested official `open-location-code-typescript`
 * library. We expose our own consistent API (matching DIGIPIN/GeoHash shape) and add bounding-box
 * decode + length-aware semantics.
 *
 * v1 will swap in our own integer-arithmetic implementation to drop the dependency.
 */

import OLCImport from "open-location-code-typescript";
import type { LatLng } from "./index.js";

// Handle both CJS interop (where the default export is on `.default`) and pure ESM
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const OLC = (OLCImport as any).default ?? OLCImport;

export const PLUSCODE_MIN_LENGTH = 4;
export const PLUSCODE_MAX_LENGTH = 12;

export type PluscodeLength = 4 | 6 | 8 | 10 | 11 | 12;

export type PluscodeCell = {
  readonly code: string;
  readonly center: LatLng;
  readonly bounds: {
    readonly minLat: number;
    readonly maxLat: number;
    readonly minLon: number;
    readonly maxLon: number;
  };
  readonly length: number;
};

const PC_ALPHABET = "23456789CFGHJMPQRVWX";
const PC_SEPARATOR = "+";
const PC_PADDING = "0";
const PC_SEPARATOR_POSITION = 8;

const VALID_CHARS = new Set([...PC_ALPHABET, PC_PADDING, PC_SEPARATOR]);

/**
 * Encode lat/lng → Plus Code at given length.
 * Default length = 10 (≈14m × 14m cell at the equator).
 */
export function encodePluscode(
  lat: number,
  lng: number,
  length: PluscodeLength = 10,
): string {
  if (lat < -90 || lat > 90) {
    throw new RangeError(`Latitude ${lat} out of range [-90, 90]`);
  }
  if (lng < -180 || lng > 180) {
    throw new RangeError(`Longitude ${lng} out of range [-180, 180]`);
  }
  if (length < PLUSCODE_MIN_LENGTH || length > PLUSCODE_MAX_LENGTH) {
    throw new RangeError(`Length ${length} out of range [4, 12]`);
  }
  if (length < 10 && length % 2 !== 0) {
    throw new RangeError(`Length ${length} must be even when below 10`);
  }
  return OLC.encode(lat, lng, length);
}

/**
 * Decode Plus Code → cell info (centre + bounds + length).
 */
export function decodePluscode(code: string): PluscodeCell {
  // Surface a clean error before letting OLC complain
  const cleaned = code.replace(/\s/g, "").toUpperCase();
  if (!cleaned.includes(PC_SEPARATOR)) {
    throw new Error(`Invalid Plus Code: missing '+' separator`);
  }
  for (const ch of cleaned) {
    if (!VALID_CHARS.has(ch)) {
      throw new Error(`Invalid Plus Code character '${ch}'`);
    }
  }

  let area;
  try {
    area = OLC.decode(cleaned);
  } catch (e) {
    throw new Error(`Invalid Plus Code: ${(e as Error).message}`);
  }

  return {
    code: cleaned,
    center: { lat: area.latitudeCenter, lng: area.longitudeCenter },
    bounds: {
      minLat: area.latitudeLo,
      maxLat: area.latitudeHi,
      minLon: area.longitudeLo,
      maxLon: area.longitudeHi,
    },
    length: area.codeLength,
  };
}

/**
 * Insert canonical "+" separator into a raw Plus Code string at position 8.
 */
export function formatPluscode(raw: string): string {
  const clean = raw.replace(/[\s+]/g, "");
  if (clean.length <= PC_SEPARATOR_POSITION) {
    return clean.padEnd(PC_SEPARATOR_POSITION, PC_PADDING) + PC_SEPARATOR;
  }
  return clean.slice(0, PC_SEPARATOR_POSITION) + PC_SEPARATOR + clean.slice(PC_SEPARATOR_POSITION);
}

/**
 * True if a string is a syntactically valid Plus Code.
 */
export function isValidPluscode(code: string): boolean {
  if (!code.includes(PC_SEPARATOR)) return false;
  return OLC.isValid(code);
}
